/**
 * No job left open — the card on top of the Today list (mockup section 3).
 * One per job still waiting for his answer. Both buttons open the question
 * on the matching screen's first step; nothing here writes.
 */
import { AlertCircle } from "lucide-react";

import { jobTitle } from "@/lib/left-open";
import { DeviceDots } from "./left-open-prompt";
import { useLeftOpen } from "./left-open-provider";

function leftAt(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

export function LeftOpenTodayCards() {
    const { on, waiting, openPrompt } = useLeftOpen();
    if (!on || waiting.length === 0) return null;
    return (
        <div className="space-y-3" data-testid="left-open-today">
            {waiting.map((j) => {
                const arrived = leftAt(j.arrivedAt);
                return (
                    <div
                        key={j.jobId}
                        className="flex flex-wrap items-center gap-4 rounded-2xl border-[3px] border-amber-700 bg-amber-50 p-4 dark:border-amber-400 dark:bg-amber-950"
                    >
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-card">
                            <AlertCircle className="h-7 w-7 text-amber-800 dark:text-amber-300" aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xl font-bold text-foreground">{jobTitle(j)} is still open</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                {j.dots.length > 0 && <DeviceDots dots={j.dots} size={14} />}
                                {arrived && <span>You got there at {arrived}</span>}
                                <span>Answer before your next job.</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                className="rounded-xl bg-blue-700 px-4 py-3 text-base font-bold text-white hover:bg-blue-800"
                                onClick={() => openPrompt(j.jobId, "app_open", "coming_back")}
                            >
                                Coming back
                            </button>
                            <button
                                type="button"
                                className="rounded-xl bg-amber-700 px-4 py-3 text-base font-bold text-white hover:bg-amber-800"
                                onClick={() => openPrompt(j.jobId, "app_open", "cant_finish")}
                            >
                                Can&apos;t finish
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
